import { message, notification } from 'antd';
import React, { createContext, useState, ReactNode, useContext, useEffect, useMemo } from 'react';
import { campaignInitialForm, CampaignTabs } from './constants/constants';
import {
  CampaignEditorBody,
  CampaignEditorResponse,
  AudienceRulesGroup,
} from 'lib.store/src/type-partner/campaigns/models';
import { ExitPageContext } from 'lib.context/src/ExitPageContext';
import {
  usePatchCampaignDataByIdMutation,
  usePostCreateCampaignByEventIdMutation,
} from 'lib.store/src/type-partner/campaigns/campaigns.api';
import {
  extractFieldErrors,
  handleProRequiredError,
  showApiError,
} from 'lib.shared/src/handleApiError';
import { partnerCampaignsActions } from 'lib.store/src/type-partner/campaigns/campaigns.slice';
import { GTMEventName } from 'lib.hooks/src/sendAnalytics/constants';
import sendAnalytics from 'lib.hooks/src/sendAnalytics/sendAnalytics';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { CreativeAdItem } from 'lib.store/src/type-partner/creatives/models';
import { getAdsFromGroups } from 'lib.store/src/type-partner/campaigns/utils/getAdsFromGroups';
import { creativeFormInitialValues } from './models/campaigns';
import {
  useLazyGetPartnerCreativesTemplatesQuery,
  usePostCreateCreativeByCampaignIdMutation,
} from 'lib.store/src/type-partner/creatives/creatives.api';
import { getInitAssetTitle } from './utils/helpers';

/**
 * Validate audience rules group - ensure no empty nodes (audiences + groups both empty)
 */
const validateAudienceRules = (group: AudienceRulesGroup | null | undefined): string | null => {
  if (!group) {
    return null; // Empty rules are valid (will be caught by strict_validation if needed)
  }

  const validate = (g: AudienceRulesGroup): string | null => {
    const audiences = g.audiences ?? [];
    const groups = g.groups ?? [];

    // Check if node is completely empty
    const hasAudiences = audiences.length > 0;
    const hasGroups = groups.length > 0;

    if (!hasAudiences && !hasGroups) {
      return 'Empty group found. Please add at least one audience or nested group.';
    }

    // Validate audience IDs
    for (let i = 0; i < audiences.length; i++) {
      const audienceId = audiences[i];
      if (!audienceId || isNaN(audienceId)) {
        return 'Invalid audience selected';
      }
    }

    // Validate nested groups recursively
    for (let i = 0; i < groups.length; i++) {
      const error = validate(groups[i]);
      if (error) return error;
    }

    return null;
  };

  return validate(group);
};

/** The campaign's status as the editor keeps it — the backend's StatusResource shape. */
type CampaignEditorStatus = {
  id: number;
  name: string;
  translate?: string;
  actions?: { id: number; slug: string; transition: number; transit: string }[];
};

interface CampaignEditorContextType {
  successNotification: ({ text }: { text: string }) => void;
  errorNotification: ({ text }: { text: string }) => void;

  formErrors: Record<string, string>;
  setFormErrors: (errors: Record<string, string>) => void;

  formValues: CampaignEditorBody;
  setFormValues: React.Dispatch<React.SetStateAction<CampaignEditorBody>>;
  handleFormChange: (form: Partial<CampaignEditorBody>) => void;
  isLoading: boolean;
  setLoading: (isLoading: boolean) => void;
  isLogoLoading: boolean;
  isCampaignEditorLoading: boolean;
  setLogoLoading: (isLoading: boolean) => void;
  isImagesLoading: boolean;
  setImagesLoading: (isLoading: boolean) => void;
  isSaving: boolean;
  setSaving: (isLoading: boolean) => void;

  updateInitialFormHandler: (response: CampaignEditorResponse) => void;

  campaignStatus: CampaignEditorStatus;
  setCampaignStatus: (data: CampaignEditorStatus) => void;

  onSubmit: ({
    strict_validation,
    launch,
    eventId,
    campaignId,
    isIdToQuery,
    redirectUrlCb,
  }: {
    strict_validation: boolean;
    launch?: boolean;
    eventId: string;
    campaignId: string | undefined;
    isIdToQuery?: boolean;
    redirectUrlCb?: (id: string) => string;
  }) => void;
  handleSaveCreative: (selectedType: number) => void;
  isSavingCreative: boolean;
  settingsAreNotCompleted: boolean;
  handleCloseSettingsNotCompletedModal: () => void;
  isSettingsNotCompletedModalOpen: boolean;
  creativesList: CreativeAdItem[];
  setCreativesList: React.Dispatch<React.SetStateAction<CreativeAdItem[]>>;
}

export const CampaignEditorContext = createContext<CampaignEditorContextType>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  successNotification: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  errorNotification: () => {},
  formErrors: {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setFormErrors: () => {},
  formValues: campaignInitialForm,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setFormValues: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  handleFormChange: () => {},
  isLoading: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setLoading: () => {},
  isLogoLoading: false,
  isCampaignEditorLoading: true,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setLogoLoading: () => {},
  isImagesLoading: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setImagesLoading: () => {},

  isSaving: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setSaving: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateInitialFormHandler: () => {},

  campaignStatus: {
    id: 0,
    name: '',
  },
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setCampaignStatus: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onSubmit: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  handleSaveCreative: () => {},
  isSavingCreative: false,
  settingsAreNotCompleted: false,
  handleCloseSettingsNotCompletedModal: () => {},
  isSettingsNotCompletedModalOpen: false,
  creativesList: [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setCreativesList: () => {},
});

interface CampaignEditorProviderProps {
  children: ReactNode;
  campaignId?: string;
  onSaveSuccess?: () => void;
  basePath?: string;
}

export const CampaignEditorProvider: React.FC<CampaignEditorProviderProps> = ({
  children,
  campaignId: campaignIdProp,
  onSaveSuccess,
  basePath = '/campaigns',
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { campaignId: campaignIdParam } = useParams();
  const campaignId = campaignIdProp ?? campaignIdParam;
  const [messageApi, contextHolder] = message.useMessage();
  const [formValues, setFormValues] = useState<CampaignEditorBody>(campaignInitialForm);
  const [formErrors, setFormErrors] = useState({});
  // LOADERS
  const [isLoading, setLoading] = useState(false);
  const [isCampaignEditorLoading, setIsCampaignEditorLoading] = useState(true);
  const [isLogoLoading, setLogoLoading] = useState(false);
  const [isImagesLoading, setImagesLoading] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [isSavingCreative, setIsSavingCreative] = useState(false);
  const [isSettingsNotCompletedModalOpen, setIsSettingsNotCompletedModalOpen] = useState(false);
  const [creativesList, setCreativesList] = useState<CreativeAdItem[]>([]);

  const [onCreateCampaign] = usePostCreateCampaignByEventIdMutation();
  const [onUpdateCampaign] = usePatchCampaignDataByIdMutation();
  const [postCreative] = usePostCreateCreativeByCampaignIdMutation();
  const [getCreativeTemplate] = useLazyGetPartnerCreativesTemplatesQuery();

  useEffect(() => {
    if (!campaignId) {
      setIsCampaignEditorLoading(false);
    }
  }, [campaignId]);

  const { isExitPageEnabled, setIsExitPageEnabled } = useContext(ExitPageContext);

  const [campaignStatus, setCampaignStatus] = useState<CampaignEditorStatus>({
    id: 0,
    name: '',
  });
  // NOTIFICATION
  const successNotification = ({ text }: { text: string }) => {
    void messageApi.open({
      type: 'success',
      content: text,
    });
  };

  const errorNotification = ({ text }: { text: string }) => {
    void messageApi.open({
      type: 'error',
      content: text,
    });
  };
  //****/

  const handleCloseSettingsNotCompletedModal = () => {
    setIsSettingsNotCompletedModalOpen(false);
  };

  const handleFormChange = (fields: CampaignEditorBody, isInit?: boolean) => {
    if (!isExitPageEnabled && !isInit) {
      // todo check
      // setIsExitPageEnabled(true)
    }
    const clearedErrors = Object.keys(fields).reduce(
      (acc, key) => {
        acc[key] = '';
        return acc;
      },
      {} as Record<string, string>,
    );
    if (fields.schedule) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        'schedule.started_at': '',
        'schedule.finished_at': '',
      }));
    }
    setFormErrors((prevErrors) => ({ ...prevErrors, ...clearedErrors }));
    setFormValues((prevFormValues) => ({ ...prevFormValues, ...fields }));
    const mergedValues = { ...formValues, ...fields };
    dispatch(
      partnerCampaignsActions.setCampaignsEditorData({
        campaignEditor: {
          ...mergedValues,
          campaign_id: campaignId || '',
        },
        selectedEvent: (mergedValues.event_id as string) || '',
        campaignId: (mergedValues.campaign_id as string) || campaignId || '',
      }),
    );
    setIsCampaignEditorLoading(false);
  };

  const updateInitialFormHandler = (response: CampaignEditorResponse) => {
    const { data } = response;
    const { audiences, conversions } = data;
    setCampaignStatus({
      id: +data.status.id,
      name: data.status.name,
      translate: data.status.translate,
      actions: data.status.actions,
    });

    handleFormChange(
      {
        ...campaignInitialForm,
        ...data,
        event_id: data.event?.id?.toString() || '',
        title: data?.title || 'Default Campaign Title',
        audiences: audiences || null,
        conversions: conversions ? conversions.map(({ id }) => id) : [],
        goal: +data.goal.id,
        landing: data.landing || null,
      },
      true,
    );
  };

  const onSubmit = async ({
    strict_validation,
    launch,
    eventId,
    campaignId,
    redirectUrl,
    isIdToQuery,
    redirectUrlCb,
  }: {
    strict_validation: boolean;
    launch?: boolean;
    eventId: string;
    campaignId: string | undefined;
    redirectUrl?: string;
    redirectUrlCb?: (id: string) => string;
    isIdToQuery?: boolean;
  }) => {
    if (!eventId) {
      setFormErrors({
        event_id: 'Please select an event first',
      });
      return;
    }

    // Validate audiences before submission
    const audiencesError = validateAudienceRules(formValues.audiences);
    if (audiencesError) {
      setFormErrors({
        audiences: audiencesError,
      });
      return;
    }

    setSaving(true);

    const conversions = formValues.conversions?.map((conversion) => {
      if (conversion && typeof conversion === 'object' && 'value' in conversion) {
        return conversion.value;
      }
      return conversion;
    });

    const landingValue =
      typeof formValues.landing === 'object'
        ? (formValues.landing?.id ?? null)
        : formValues.landing || null;

    const payload: CampaignEditorBody = {
      campaign_id: campaignId || formValues.campaign_id,
      strict_validation,
      launch,
      event_id: eventId as unknown as string,
      title: formValues.title,
      audiences: formValues.audiences || null,
      conversions: conversions,
      goal: formValues.goal,
      schedule: formValues.schedule,
      is_audience_expansion: formValues.is_audience_expansion,
      landing: landingValue,
      website_url: formValues.website_url?.trim() || null,
    };

    try {
      const updatePayload = {
        ...payload,
      };

      const response = payload.campaign_id
        ? await onUpdateCampaign(updatePayload)
        : await onCreateCampaign(payload);

      if ('error' in response) {
        setSaving(false);
        // a launch refused for want of Boost PRO opens the paywall, not the form errors
        if (handleProRequiredError(response)) return;
        const errors = extractFieldErrors(response.error);
        errors && setFormErrors(errors);
        const isErrorsEmpty = !Object.values(errors).length;
        const errorMessage = isErrorsEmpty
          ? (response as { error?: { data?: { message?: string } } })?.error?.data?.message
          : 'Please fix validation errors before launching';
        notification.open({
          message: errorMessage,
          placement: 'bottomLeft',
          closeIcon: false,
          type: 'error',
        });
      } else {
        dispatch(partnerCampaignsActions.setTemporaryEditorData(null));
        if (isExitPageEnabled) {
          setIsExitPageEnabled(false);
        }
        sendAnalytics(GTMEventName.saveCampaign);
        setSaving(false);
        if (response.data?.data?.status) {
          const s = response.data.data.status;
          setCampaignStatus({
            id: +s.id,
            name: s.name,
            translate: s.translate,
            actions: s.actions,
          });
        }
        if (!campaignId) {
          dispatch(
            partnerCampaignsActions.setCampaignsEditorData({
              campaignEditor: null,
              selectedEvent: null,
              campaignId: '',
            }),
          );
        }

        notification.open({
          message: (
            <span>
              Campaign <b>{formValues.title}</b> successfully{' '}
              {payload.campaign_id ? 'updated' : 'created'}
            </span>
          ),
          placement: 'bottomLeft',
          closeIcon: false,
          type: 'success',
        });

        if (onSaveSuccess) {
          return onSaveSuccess();
        }

        if (redirectUrlCb) {
          return navigate(redirectUrlCb(response.data.data.id));
        }

        if (redirectUrl) {
          if (isIdToQuery) {
            return navigate(`${redirectUrl}?campaignId=${response.data.data.id}`);
          }
          return navigate(redirectUrl);
        }

        return navigate(basePath);
      }
    } catch (error) {
      if (!campaignId) {
        dispatch(
          partnerCampaignsActions.setCampaignsEditorData({
            campaignEditor: formValues,
            selectedEvent: eventId as unknown as string,
            campaignId,
          }),
        );
      }
      setSaving(false);
      showApiError(error, 'Save error');
    }
  };

  const settingsAreNotCompleted = useMemo(() => {
    if (!campaignId) {
      return false;
    }
    // the ads need somewhere to send clicks: a landing page, the campaign's own
    // link, or every group sending them elsewhere (lead forms, their own links)
    const hasDestination =
      !!formValues.landing || !!formValues.website_url || formValues.needs_landing === false;
    return !hasDestination || !formValues.event_id;
  }, [campaignId, formValues]);

  const handleSaveCreative = async (selectedType: number) => {
    console.log('selectedType', selectedType);
    setIsSavingCreative(true);
    if (settingsAreNotCompleted) {
      setIsSettingsNotCompletedModalOpen(true);
      setIsSavingCreative(false);
      return;
    }

    if (!campaignId) {
      setIsSavingCreative(false);
      return;
    }

    const currentEventId = (formValues.event_id as string) || '';
    const creativeTemplateRes = await getCreativeTemplate(
      { eventId: currentEventId, type: selectedType || '' },
      { skip: !selectedType || !currentEventId },
    );

    // todo get template
    const creativeData = {
      ...creativeFormInitialValues,
      title: getInitAssetTitle(selectedType.toString() || '', creativesList),
      type: +(selectedType || 0),
      template: creativeTemplateRes?.data?.data?.template || '',
    };
    try {
      const response = await postCreative({ campaignId, data: creativeData });
      if ('data' in response) {
        const ads = getAdsFromGroups(response?.data?.data?.groups);
        const newCreativeId = ads[ads.length - 1]?.id;
        setCreativesList(ads);
        navigate(`${basePath}/${campaignId}/${CampaignTabs.Creatives}/${newCreativeId}`);
        // notification.open({
        //     message: (
        //         <>
        //             <div>
        //                 Creative submitted for moderation.
        //             </div>
        //             <div onClick={() => setIsAddCreativeModalOpen(true)} className="text-link inline">
        //                 Add new creative
        //             </div>
        //         </>

        //     ),
        //     placement: 'bottomLeft',
        //     closeIcon: false,
        //     type: 'success',
        // });

        setIsSavingCreative(false);
      }
      if ('error' in response) {
        showApiError(response, 'Creative saving error');
        setIsSavingCreative(false);
      }
    } catch (error) {
      void message.open({
        type: 'error',
        content: 'Creative saving error',
      });
      setIsSavingCreative(false);
    }
  };

  return (
    <CampaignEditorContext.Provider
      value={{
        successNotification,
        errorNotification,
        formErrors,
        setFormErrors,

        handleFormChange,
        formValues,
        setFormValues,
        // LOADERS
        isLoading,
        setLoading,
        isSaving,
        setSaving,
        isLogoLoading,
        setLogoLoading,
        isImagesLoading,
        setImagesLoading,

        updateInitialFormHandler,
        isCampaignEditorLoading,

        campaignStatus,
        setCampaignStatus,
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit,
        handleSaveCreative,
        isSavingCreative,
        settingsAreNotCompleted,
        handleCloseSettingsNotCompletedModal,
        isSettingsNotCompletedModalOpen,
        creativesList,
        setCreativesList,
      }}
    >
      <>
        {contextHolder}
        {children}
      </>
    </CampaignEditorContext.Provider>
  );
};
